import { Box, Link, Stack, Typography, useTheme } from "@mui/material";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import { useReassistRequestDetail } from "../api/useReassistRequests";
import {
  type ReassistRequestAmount,
  type ReassistRequestItem,
  type ReassistRequestSummary,
} from "../types";
import {
  costItemSx,
  getStatusStyle,
  INNER_RADIUS,
  rowHoverBg,
  TEXT,
} from "../utils/tokens";
import { AmountText } from "./AmountText";
import { ReassistButton } from "./ReassistButton";
import { isHttpUrl } from "@/utils/link";

/** 書類の内訳（書類名・役所・状態）。 */
/**
 * 消費税と税込合計の行。取得手数料（税抜）にだけ消費税がかかり、取得実費は非課税。
 * 暫定で消費税を求められない場合は何も出さない。
 */
const TaxLines: React.FC<{
  amount: ReassistRequestAmount;
  isProvisional: boolean;
}> = ({ amount, isProvisional }) => {
  const theme = useTheme();
  if (amount.tax === null || amount.total === null) return null;
  return (
    <>
      <Box sx={costItemSx(theme)}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
          消費税（取得手数料の10%）
        </Typography>
        <AmountText
          amount={amount.tax}
          isProvisional={isProvisional}
          showChip={false}
        />
      </Box>
      <Box sx={costItemSx(theme)}>
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          税込合計
        </Typography>
        <AmountText
          amount={amount.total}
          isProvisional={isProvisional}
          size="md"
          showChip={false}
        />
      </Box>
    </>
  );
};

const ItemList: React.FC<{
  requestId: string;
  items: ReassistRequestItem[];
}> = ({ requestId, items }) => {
  const theme = useTheme();
  return (
    <Stack
      component="ul"
      spacing={1}
      sx={{ listStyle: "none", m: 0, p: 0 }}
      aria-label="依頼した書類"
    >
      {items.map((item, index) => {
        const style = getStatusStyle(theme, item.status);
        const unavailable = item.unavailableReason ?? "";
        const hearingResult = item.hearingResult ?? "";
        return (
          <Box
            component="li"
            key={`${requestId}-${index}`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              px: 1.5,
              py: 1.25,
              backgroundColor: theme.palette.white.main,
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: INNER_RADIUS,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.primary", overflowWrap: "anywhere" }}
              >
                {item.label}
              </Typography>
              {item.location !== "" && (
                <Typography
                  component="div"
                  sx={{
                    ...TEXT.meta,
                    mt: 0.25,
                    color: "text.secondary",
                    overflowWrap: "anywhere",
                  }}
                >
                  {item.location}
                </Typography>
              )}
              {item.itemType === "HEARING" && hearingResult !== "" && (
                <Typography
                  component="div"
                  sx={{
                    ...TEXT.meta,
                    mt: 0.25,
                    color: "text.primary",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  回答：{hearingResult}
                </Typography>
              )}
              {unavailable !== "" && (
                <Typography
                  component="div"
                  sx={{
                    ...TEXT.meta,
                    mt: 0.25,
                    color: "error.main",
                    overflowWrap: "anywhere",
                  }}
                >
                  取得できませんでした：{unavailable}
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              gap={0.75}
              sx={{ alignItems: "center", flexShrink: 0, pt: "3px" }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  flexShrink: 0,
                  borderRadius: "50%",
                  backgroundColor:
                    unavailable !== "" ? "error.main" : style.dot,
                }}
              />
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  color: unavailable !== "" ? "error.main" : style.color,
                }}
              >
                {unavailable !== "" ? "取得不可" : item.statusLabel}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

interface Props {
  request: ReassistRequestSummary;
}

/**
 * 依頼1件の内訳。書類の一覧、金額の内訳、納品済みの完全版（黒塗りなし）を出す。
 * 開いているときだけ描画する（PDFのダウンロードURLの有効期限が短く、開くたびに取得するため）。
 */
export const RequestDetailBody: React.FC<Props> = ({ request }) => {
  const theme = useTheme();
  const teal = theme.palette.primary.main;
  const {
    data: detail,
    error: detailError,
    retry,
  } = useReassistRequestDetail(request.requestId);
  const count = request.documentCount ?? request.items.length;

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography
          component="h4"
          variant="body2"
          sx={{ mb: 1, fontWeight: 600, color: "text.secondary" }}
        >
          依頼した書類（{count}件）
        </Typography>
        {request.items.length === 0 ? (
          <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
            <DescriptionOutlined
              aria-hidden
              sx={{ fontSize: 20, color: "text.secondary" }}
            />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              書類の内訳は記録されていません
            </Typography>
          </Stack>
        ) : (
          <ItemList requestId={request.requestId} items={request.items} />
        )}
      </Box>
      {request.amount.isProvisional && (
        <Box>
          <Box sx={costItemSx(theme)}>
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              暫定額（消費税を除く）
            </Typography>
            <AmountText
              amount={request.amount.subtotal}
              isProvisional
              showChip={false}
            />
          </Box>
          <TaxLines amount={request.amount} isProvisional />
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1, alignItems: "flex-start" }}
          >
            <InfoOutlined
              aria-hidden
              sx={{
                fontSize: 16,
                mt: "4px",
                flexShrink: 0,
                color: "text.secondary",
              }}
            />
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.7, color: "text.secondary" }}
            >
              この金額は暫定です。納品時に確定額に変わります。
            </Typography>
          </Stack>
        </Box>
      )}
      {!request.amount.isProvisional && request.amount.handlingFee !== null && (
        <Box sx={costItemSx(theme)}>
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 500, wordBreak: "break-word" }}
          >
            <Box
              component="span"
              sx={{ fontWeight: 400, color: "text.secondary" }}
            >
              確定額：
            </Box>
            {`取得手数料 ${request.amount.handlingFee.toLocaleString()}円 ＋ 取得実費 ${(
              request.amount.expense ?? 0
            ).toLocaleString()}円`}
          </Typography>
          <AmountText
            amount={request.amount.subtotal}
            isProvisional={false}
            showChip={false}
          />
        </Box>
      )}
      {!request.amount.isProvisional && request.amount.handlingFee !== null && (
        <TaxLines amount={request.amount} isProvisional={false} />
      )}
      {detail === undefined && detailError !== undefined && (
        <Box role="alert">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            納品資料を読み込めませんでした。
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <ReassistButton
              label="再読み込み"
              variant="text"
              size="small"
              onClick={retry}
            />
          </Box>
        </Box>
      )}
      {detail !== undefined && detail.deliveredFiles.length > 0 && (
        <Box>
          <Typography
            component="h4"
            variant="body2"
            sx={{
              mb: 0.75,
              fontWeight: 600,
              color: "text.secondary",
            }}
          >
            納品資料（完全版）
          </Typography>
          {/* http / https 以外のURL（javascript: など）はリンクにしない */}
          {detail.deliveredFiles
            .filter((file) => isHttpUrl(file.url))
            .map((file) => (
              <Link
                key={file.url}
                variant="body2"
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${file.fileName}（新しいタブで開く）`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: 44,
                  px: 1.25,
                  mt: 0.75,
                  wordBreak: "break-all",
                  textDecoration: "none",
                  color: "primary.main",
                  borderRadius: INNER_RADIUS,
                  border: `1px solid ${theme.palette.border.main}`,
                  backgroundColor: theme.palette.white.main,
                  "&:hover": {
                    backgroundColor: rowHoverBg(theme),
                    textDecoration: "underline",
                  },
                }}
              >
                <PictureAsPdfOutlined
                  aria-hidden
                  sx={{ fontSize: 20, flexShrink: 0, color: teal }}
                />
                {file.fileName}
                <OpenInNew
                  aria-hidden
                  sx={{
                    fontSize: 16,
                    ml: "auto",
                    flexShrink: 0,
                    color: "text.secondary",
                  }}
                />
              </Link>
            ))}
        </Box>
      )}
    </Stack>
  );
};
